import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PawLinkQR, { renderBrandedQrCanvas, downloadBrandedSvg } from "@/components/PawLinkQR";
import {
    ArrowLeft, PawPrint, ShieldCheck, Download, Printer, ShareNetwork,
    LockKey, Sparkle, FilePdf, Check, MapPin
} from "@phosphor-icons/react";
import jsPDF from "jspdf";
import PageShell from "@/components/PageShell";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useIsStoreApp, useHasProAccess } from "@/lib/storeAppDetection";
import { track } from "@/lib/analytics";
import { finderUrl } from "@/lib/petUtils";

/* -------- helpers (mirrors QRCard) -------- */
const BRAND_FOREST = [30, 58, 47];
const BRAND_TERRA = [224, 122, 95];
const BRAND_BONE = [246, 241, 234];
const BRAND_INK = [26, 32, 30];
const BRAND_INK2 = [110, 120, 116];

// Paw-frame aspect ratio (height / width) — matches PawLinkQR layout.
const PAW_ASPECT = 1;

function loadImg(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

/* ================================================== */
export default function PetQRPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const { t } = useI18n();
    const isStoreApp = useIsStoreApp();
    const [pet, setPet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(null); // "png" | "pdf" | "print" | "share"
    const [copied, setCopied] = useState(false);
    const qrHolderRef = useRef(null);
    const isPremium = user?.plan === "premium";
    // Early Access grants all download/print/share actions on the Google
    // Play Android build so users experience PawLink fully during launch.
    // Backend Premium record is untouched — this is a UI unlock only.
    const hasAccess = useHasProAccess(user);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/pets/${id}`);
                setPet(data);
                // GA4: individual pet QR page view — separate from raw
                // page_view so the "QR page visits" metric is unambiguous.
                track("qr_page_view", { pet_id: data.id, public_id: data.public_id });
            } catch (e) {
                setError("Pet not found");
            } finally { setLoading(false); }
        })();
    }, [id]);

    const url = pet ? finderUrl(pet.public_id) : "";
    const filename = pet ? `pawlink-${(pet.name || pet.id).replace(/\s+/g, "-").toLowerCase()}` : "pawlink";
    const publicIdShort = pet?.public_id?.toUpperCase().slice(0, 8) || "";

    const downloadPng = async () => {
        if (!hasAccess || busy) return;
        setBusy("png");
        try {
            const { dataUrl } = await renderBrandedQrCanvas(url, 1600, {
                avatarUrl: pet?.photo_path ? fileUrl(pet.photo_path) : undefined,
            });
            const link = document.createElement("a");
            link.download = `${filename}.png`;
            link.href = dataUrl;
            document.body.appendChild(link); link.click(); link.remove();
            track("qr_downloaded", { format: "png", pet_id: pet?.id, source: "pet_qr_page", early_access: !isPremium && isStoreApp });
        } finally { setBusy(null); }
    };

    const downloadSvg = async () => {
        if (!hasAccess || busy) return;
        setBusy("svg");
        try {
            await downloadBrandedSvg(url, filename, {
                pixelSize: 1600,
                avatarUrl: pet?.photo_path ? fileUrl(pet.photo_path) : undefined,
            });
        } finally { setBusy(null); }
    };

    const downloadPdf = async () => {
        if (!hasAccess || busy || !pet) return;
        setBusy("pdf");
        try {
            const { dataUrl: qrData } = await renderBrandedQrCanvas(url, 1600, {
                avatarUrl: pet?.photo_path ? fileUrl(pet.photo_path) : undefined,
            });

            let petPhotoData = null;
            if (pet.photo_path) {
                try {
                    const img = await loadImg(fileUrl(pet.photo_path));
                    const c = document.createElement("canvas");
                    const s = 240;
                    c.width = s; c.height = s;
                    const cctx = c.getContext("2d");
                    cctx.fillStyle = "#FFFFFF"; cctx.fillRect(0, 0, s, s);
                    const ratio = Math.max(s / img.width, s / img.height);
                    const w = img.width * ratio, h = img.height * ratio;
                    cctx.drawImage(img, (s - w) / 2, (s - h) / 2, w, h);
                    petPhotoData = c.toDataURL("image/jpeg", 0.82);
                } catch (_) { petPhotoData = null; }
            }

            const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
            const pageW = doc.internal.pageSize.getWidth();
            const pageH = doc.internal.pageSize.getHeight();
            const margin = 14;

            doc.setDrawColor(...BRAND_FOREST); doc.setLineWidth(0.6);
            doc.roundedRect(margin, margin, pageW - margin * 2, pageH - margin * 2, 6, 6, "S");

            const headerY = margin + 8, headerH = 22;
            doc.setFillColor(...BRAND_FOREST);
            doc.roundedRect(margin + 6, headerY, pageW - (margin + 6) * 2, headerH, 4, 4, "F");
            doc.setFont("helvetica", "bold"); doc.setFontSize(18); doc.setTextColor(...BRAND_BONE);
            doc.text("PawLink", margin + 12, headerY + headerH / 2 + 1, { baseline: "middle" });
            doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(233, 220, 200);
            doc.text("Smart QR Pet ID", pageW - margin - 12, headerY + headerH / 2 + 1, { align: "right", baseline: "middle" });

            let cursorY = headerY + headerH + 14;
            doc.setFont("helvetica", "bold"); doc.setFontSize(22); doc.setTextColor(...BRAND_INK);
            doc.text("If found, please scan", pageW / 2, cursorY, { align: "center" });
            cursorY += 9;
            doc.setFont("helvetica", "normal"); doc.setFontSize(13); doc.setTextColor(...BRAND_TERRA);
            doc.text(pet.name, pageW / 2, cursorY, { align: "center" });
            cursorY += 6;

            if (petPhotoData) {
                const size = 24, px = (pageW - size) / 2;
                doc.setDrawColor(...BRAND_FOREST); doc.setLineWidth(0.4);
                doc.roundedRect(px - 1, cursorY - 1, size + 2, size + 2, 3, 3, "S");
                doc.addImage(petPhotoData, "JPEG", px, cursorY, size, size, undefined, "FAST");
                cursorY += size + 6;
            } else { cursorY += 2; }

            const qrW = 92, qrH = 92 * PAW_ASPECT, qrX = (pageW - qrW) / 2, qrY = cursorY;
            doc.setFillColor(255, 255, 255);
            doc.roundedRect(qrX - 6, qrY - 6, qrW + 12, qrH + 12, 4, 4, "F");
            doc.setDrawColor(...BRAND_FOREST); doc.setLineWidth(0.5);
            doc.roundedRect(qrX - 6, qrY - 6, qrW + 12, qrH + 12, 4, 4, "S");
            doc.addImage(qrData, "PNG", qrX, qrY, qrW, qrH, undefined, "FAST");

            const belowQrY = qrY + qrH + 14;
            doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...BRAND_TERRA);
            doc.text("SCAN HERE", pageW / 2, belowQrY, { align: "center" });
            doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(...BRAND_INK2);
            doc.text("This tag is protected by PawLink — scan to see this pet's photo, allergies, medical notes and contact the owner in one tap.", pageW / 2, belowQrY + 7, { align: "center", maxWidth: 150 });

            const urlY = belowQrY + 22;
            doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...BRAND_INK2);
            doc.text("Or visit", pageW / 2, urlY, { align: "center" });
            doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...BRAND_FOREST);
            doc.text(url, pageW / 2, urlY + 6, { align: "center" });

            const footerH = 12, footerY = pageH - margin - 6 - footerH;
            doc.setFillColor(...BRAND_BONE);
            doc.roundedRect(margin + 6, footerY, pageW - (margin + 6) * 2, footerH, 3, 3, "F");
            doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(...BRAND_FOREST);
            doc.text("PawLink — Smart QR Pet ID · getpawlink.com", pageW / 2, footerY + footerH / 2 + 1, { align: "center", baseline: "middle" });

            doc.save(`${filename}.pdf`);
            track("qr_downloaded", { format: "pdf", pet_id: pet.id, source: "pet_qr_page", early_access: !isPremium && isStoreApp });
        } finally { setBusy(null); }
    };

    const printQR = () => {
        if (!hasAccess || busy) return;
        track("qr_printed", { pet_id: pet?.id, source: "pet_qr_page", early_access: !isPremium && isStoreApp });
        window.print();
    };

    const shareQR = async () => {
        if (busy || !pet) return;
        setBusy("share");
        try {
            const shareData = {
                title: `${t("qr_detail.share_title_prefix")} ${pet.name}`,
                text: t("qr_detail.share_text"),
                url,
            };
            if (navigator.share) {
                try { await navigator.share(shareData); }
                catch (_) { /* user cancelled */ }
            } else {
                try {
                    await navigator.clipboard.writeText(url);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                } catch (_) { /* ignore */ }
            }
        } finally { setBusy(null); }
    };

    if (loading) return (
        <PageShell testId="qr-detail-loading">
            <p className="text-paw-ink2 dark:text-paw-bone/60">Loading…</p>
        </PageShell>
    );

    if (error || !pet) return (
        <PageShell testId="qr-detail-error">
            <p className="text-paw-ink2 dark:text-paw-bone/60">{error || "Pet not found"}</p>
            <Link to="/pets" className="mt-4 inline-flex btn-primary">
                <ArrowLeft size={16} /> <span className="ms-1.5">{t("qr_detail.back")}</span>
            </Link>
        </PageShell>
    );

    return (
        <PageShell testId="qr-detail-page" maxWidth="max-w-md">
            {/* Back */}
            <Link to="/pets" data-testid="qr-detail-back"
                className="inline-flex items-center gap-1.5 text-sm text-paw-ink2 dark:text-paw-bone/60 hover:text-paw-ink dark:hover:text-paw-bone transition -mt-2 mb-5">
                <ArrowLeft size={14} weight="bold" /> {t("qr_detail.back")}
            </Link>

            {/* Single premium QR card */}
            <section
                data-testid="qr-detail-hero-card"
                className="relative rounded-[2rem] overflow-hidden bg-white dark:bg-[hsl(152,12%,11%)] border border-paw-line dark:border-white/10 shadow-hover"
            >
                {/* Slim header */}
                <div className="flex items-center justify-between px-5 sm:px-6 pt-5 pb-4">
                    <div className="inline-flex chip bg-paw-forest/8 dark:bg-paw-terra/12 text-paw-forest dark:text-paw-terra">
                        <ShieldCheck size={12} weight="fill" /> {t("qr_detail.kicker")}
                    </div>
                    <span
                        data-testid="qr-detail-id"
                        className="font-mono text-[10px] sm:text-xs tracking-[0.2em] text-paw-ink2 dark:text-paw-bone/60"
                    >
                        {t("qr_detail.pet_id")} · {publicIdShort}
                    </span>
                </div>

                {/* Pet identity — compact, centered */}
                <div className="px-5 sm:px-6 pb-4 flex flex-col items-center text-center">
                    <div className="w-20 h-20 rounded-full ring-4 ring-white dark:ring-[hsl(152,12%,10%)] shadow-hover overflow-hidden bg-paw-muted dark:bg-white/5 grid place-items-center text-paw-forest dark:text-paw-terra">
                        {pet.photo_path ? (
                            <img
                                src={fileUrl(pet.photo_path)}
                                alt={pet.name}
                                className="pet-photo w-full h-full object-cover"
                                data-testid="qr-detail-photo"
                            />
                        ) : (
                            <PawPrint size={34} weight="duotone" />
                        )}
                    </div>
                    <h1 className="mt-3 font-display text-2xl sm:text-3xl tracking-tight text-paw-ink dark:text-paw-bone leading-tight" data-testid="qr-detail-name">
                        {pet.name}
                    </h1>
                    <p className="mt-0.5 text-xs sm:text-sm text-paw-ink2 dark:text-paw-bone/60 capitalize">
                        {t(`petform.type_${pet.type}`)}
                        {pet.breed ? ` · ${pet.breed}` : ""}
                        {pet.city ? <> · <MapPin size={10} weight="fill" className="inline align-middle" /> {pet.city}</> : ""}
                    </p>
                </div>

                {/* Big QR — Premium PawLink paw-shaped QR */}
                <div className="px-5 sm:px-8 pb-6 flex justify-center">
                    <div ref={qrHolderRef} data-testid="qr-detail-qr" className="relative p-4 sm:p-5 bg-white rounded-[1.75rem] border border-paw-line shadow-hover">
                        <PawLinkQR value={url} size={244} avatarUrl={pet?.photo_path ? fileUrl(pet.photo_path) : undefined} />
                        <div data-testid="qr-detail-live" className="absolute -top-2 -end-2 inline-flex items-center gap-1 rounded-full bg-paw-forest text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 shadow-hover">
                            <span className="w-1.5 h-1.5 rounded-full bg-paw-terra paw-pulse" aria-hidden />
                            {t("qr_detail.live_indicator")}
                        </div>
                    </div>
                </div>

                {/* Reassure line */}
                <p data-testid="qr-detail-reassure" className="px-6 pb-5 text-xs sm:text-sm text-paw-ink2 dark:text-paw-bone/60 text-center italic max-w-sm mx-auto leading-snug">
                    {t("qr_detail.passport_reassure", { name: pet.name })}
                </p>

                {/* Actions */}
                <div className="border-t border-paw-line dark:border-white/10 p-4 sm:p-5" data-testid="qr-detail-actions">
                    {hasAccess ? (
                        <div className="grid grid-cols-2 gap-2 sm:gap-3">
                            <ActionButton icon={<Download size={18} weight="fill" />} label={t("qr_detail.actions_download_png")} onClick={downloadPng} busy={busy === "png"} testId="qr-detail-download-png" />
                            <ActionButton icon={<Download size={18} weight="fill" />} label="SVG" onClick={downloadSvg} busy={busy === "svg"} testId="qr-detail-download-svg" />
                            <ActionButton icon={<FilePdf size={18} weight="fill" />} label={t("qr_detail.actions_download_pdf")} onClick={downloadPdf} busy={busy === "pdf"} testId="qr-detail-download-pdf" primary />
                            <ActionButton icon={<Printer size={18} weight="fill" />} label={t("qr_detail.actions_print")} onClick={printQR} busy={busy === "print"} testId="qr-detail-print" />
                            <ActionButton icon={copied ? <Check size={18} weight="bold" /> : <ShareNetwork size={18} weight="fill" />} label={copied ? t("safety.copied") : t("qr_detail.actions_share")} onClick={shareQR} busy={busy === "share"} testId="qr-detail-share" />
                        </div>
                    ) : (
                        <div data-testid="qr-detail-premium-gate" className="rounded-3xl border border-paw-terra/25 bg-paw-terra/8 dark:bg-paw-terra/12 p-5 flex flex-col items-start gap-3">
                            <div className="flex items-center gap-3">
                                <div className="w-11 h-11 rounded-2xl bg-paw-terra/15 text-paw-terra grid place-items-center flex-shrink-0">
                                    <LockKey size={20} weight="duotone" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-display text-base sm:text-lg text-paw-ink dark:text-paw-bone leading-tight">{t("qr_detail.premium_gate_h", { name: pet.name })}</p>
                                    <p className="mt-1 text-xs sm:text-sm text-paw-ink2 dark:text-paw-bone/70">{t("qr_detail.premium_gate_sub", { name: pet.name })}</p>
                                </div>
                            </div>
                            {/* Google Play compliance: hide the Unlock CTA
                                in store-app builds. The gated download UI
                                stays visible so users know a premium feature
                                exists — just no purchase surface. */}
                            {!isStoreApp && (
                                <Link to="/subscription" data-testid="qr-detail-upgrade" className="btn-primary text-sm w-full justify-center">
                                    <Sparkle size={14} weight="fill" /> <span className="ms-1.5">{t("qr_detail.premium_gate_cta")}</span>
                                </Link>
                            )}
                        </div>
                    )}
                </div>
            </section>

            {/* Print stylesheet — show only the QR + name when printing */}
            <style>{`
                @media print {
                    body * { visibility: hidden; }
                    [data-testid="qr-detail-hero-card"], [data-testid="qr-detail-hero-card"] * { visibility: visible; }
                    [data-testid="qr-detail-hero-card"] { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none; border: none; }
                    [data-testid="qr-detail-actions"] { display: none !important; }
                }
            `}</style>
        </PageShell>
    );
}

function ActionButton({ icon, label, onClick, busy, testId, primary }) {
    const base = "flex flex-col items-center justify-center gap-1.5 rounded-2xl py-3 px-2 text-xs font-medium transition text-center leading-tight disabled:opacity-60";
    const styles = primary
        ? "bg-paw-forest text-white dark:bg-paw-terra hover:opacity-95"
        : "border border-paw-line dark:border-white/10 text-paw-ink dark:text-paw-bone hover:bg-paw-muted dark:hover:bg-white/5";
    return (
        <button type="button" onClick={onClick} disabled={busy} data-testid={testId}
            className={`${base} ${styles}`}>
            <span className={primary ? "text-white" : "text-paw-forest dark:text-paw-terra"}>{icon}</span>
            {busy ? "…" : label}
        </button>
    );
}
